import { useEffect, useMemo, useRef } from "react";

import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";

import { useCompanyQuota } from "app/pages/sales-intelligence/modules/quota/hooks/useCompanyQuota";
import { useQuotaQueryClient } from "app/pages/sales-intelligence/modules/quota/hooks/useQuotaQueryClient";
import salesApiService from "app/pages/sales-intelligence/services/salesApiService";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";

import { companyAnalysisQueryKeys } from "./queryKeyFactory";

export const useCompanyDomainInfoQuery = (domain: string, isEnabled = true) => {
    const queryClient = useQueryClient();
    const quotaQueryClient = useQuotaQueryClient();
    const isQueryEnabled = isEnabled && salesSettingsHelper.isSiUser();

    const lastDomainRef = useRef<string | null>(null);
    const { isQuotaReached, isQuotaUnlimited } = useCompanyQuota(isQueryEnabled);

    const normalizedDomain = useMemo(() => (domain ? domain.trim().toLowerCase() : ""), [domain]);

    const queryResult = useQuery({
        queryKey: companyAnalysisQueryKeys.companyDomainInfo(normalizedDomain),
        enabled: !!normalizedDomain && isQueryEnabled,
        queryFn: async ({ signal }) => {
            const data = await salesApiService.companyAnalysis.fetchCompanyDomainInfo(domain, {
                cancellation: signal,
            });
            return data;
        },
        placeholderData: keepPreviousData,
    });

    useEffect(() => {
        if (queryResult.data && normalizedDomain) {
            queryClient.setQueryData(companyAnalysisQueryKeys.companyBaseInfo(normalizedDomain), queryResult.data);
        }
    }, [queryResult.data, normalizedDomain, queryClient]);

    useEffect(() => {
        if (
            isQueryEnabled &&
            queryResult.isSuccess &&
            normalizedDomain &&
            lastDomainRef.current !== normalizedDomain &&
            !isQuotaUnlimited &&
            !isQuotaReached
        ) {
            lastDomainRef.current = normalizedDomain;
            void quotaQueryClient.invalidateCompanyInformationQuota();

            if (salesSettingsHelper.isDataCreditsEnabled) {
                void quotaQueryClient.invalidateDataCreditsState();
            }
        }
    }, [isQueryEnabled, queryResult.isSuccess, normalizedDomain, isQuotaUnlimited, isQuotaReached, quotaQueryClient]);

    return queryResult;
};
